import type { SeatPhotoRuleEntry } from '../consts/seatPhotoRules';

export type BuildPreviewParams = {
  seatName: string;
  seatNumber?: number | string;
  rowName?: number | string;
};

const CDN_BASE = 'https://storage.yandexcloud.net/navkaarena/photos-seats-ranges';

const normalizeSectorKey = (seatName: string): string | null => {
  const normalized = seatName.trim().toUpperCase();

  const galleryMatch = normalized.match(/ГАЛЕРЕЯ\s+([A-ZА-Я]\d?)/i);
  if (galleryMatch) return `GALLERY_${galleryMatch[1].toUpperCase()}`;

  const parterMatch = normalized.match(/ПАРТЕР\s+([A-ZА-Я]\d?)/i);
  if (parterMatch) return `PARTER_${parterMatch[1].toUpperCase()}`;

  const royalVipMatch = normalized.match(/ROYAL\s+VIP\s*\((\d+)\s*ПЕРСОН\)/i);
  if (royalVipMatch) return `ROYAL_VIP_${royalVipMatch[1]}`;

  const sectorMatch = normalized.match(/СЕКТОР\s+([A-ZА-Я]\d)/i);
  if (sectorMatch) return sectorMatch[1].toUpperCase();

  const simpleMatch = normalized.match(/\b([A-ZА-Я]\d)\b/);
  if (simpleMatch) return simpleMatch[1].toUpperCase();

  return null;
};

const toNumber = (value?: number | string | null): number | null => {
  if (value === undefined || value === null) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

export const normalizeSeatPhotoRules = (
  seatPhotoRules: SeatPhotoRuleEntry[] | string | null | undefined
): SeatPhotoRuleEntry[] | null => {
  if (Array.isArray(seatPhotoRules)) return seatPhotoRules;

  if (typeof seatPhotoRules === 'string') {
    try {
      const parsed = JSON.parse(seatPhotoRules);
      return Array.isArray(parsed) ? (parsed as SeatPhotoRuleEntry[]) : null;
    } catch {
      return null;
    }
  }

  return null;
};

const parseRowRange = (rows: string): Array<[number, number]> => {
  if (rows.trim() === '*' || rows.trim() === '')
    return [[Number.NEGATIVE_INFINITY, Number.POSITIVE_INFINITY]];

  return rows
    .split(',')
    .map(part => part.trim())
    .filter(Boolean)
    .map(part => {
      if (part.includes('-')) {
        const [from, to] = part.split('-').map(n => Number(n.trim()));
        return [from, to] as [number, number];
      }
      const single = Number(part);
      return [single, single] as [number, number];
    });
};

const parseSeatRange = (seats: string): [number, number] => {
  if (seats.trim() === '*' || seats.trim() === '') {
    return [Number.NEGATIVE_INFINITY, Number.POSITIVE_INFINITY];
  }
  if (!seats.includes('-')) {
    const single = Number(seats.trim());
    return [single, single];
  }
  const [from, to] = seats.split('-').map(n => Number(n.trim()));
  return [from, to];
};

const matchRows = (rows: string, row: number): boolean =>
  parseRowRange(rows).some(([from, to]) => row >= from && row <= to);

const matchSeats = (seats: string, seat: number): boolean => {
  const [from, to] = parseSeatRange(seats);
  return seat >= from && seat <= to;
};

const extractRowFromSeatName = (seatName: string): number | null => {
  const match = seatName.match(/ряд\s+(\d+)/i);
  return match ? Number(match[1]) : null;
};

const findRule = (
  sector: string,
  row: number | null,
  seat: number | null,
  rules: SeatPhotoRuleEntry[]
): SeatPhotoRuleEntry | null => {
  const candidates = rules.filter(rule => rule.sector === sector);
  if (!candidates.length) return null;

  if (row === null || seat === null) {
    return candidates[0];
  }

  return candidates.find(rule => matchRows(rule.rows, row) && matchSeats(rule.seats, seat)) ?? null;
};

export const buildSeatPreviewUrl = (
  { seatName, seatNumber, rowName }: BuildPreviewParams,
  seatPhotoRulesInput: SeatPhotoRuleEntry[] | string | null | undefined
): string | null => {
  const seatPhotoRules = normalizeSeatPhotoRules(seatPhotoRulesInput);
  if (!seatPhotoRules || seatPhotoRules.length === 0) return null;

  const sectorKey = normalizeSectorKey(seatName);
  if (!sectorKey) return null;

  const numericSeat = toNumber(seatNumber);
  const numericRow = toNumber(rowName) ?? extractRowFromSeatName(seatName);

  const rule = findRule(sectorKey, numericRow, numericSeat, seatPhotoRules);
  if (!rule) return null;

  return `${CDN_BASE}/${rule.file}`;
};
